import { ResourceLoader } from "../../widgets/resource_loader";
import { createSignal, For, Show } from "solid-js";
import { getJson } from "../../data/static";
import { BackButton } from "../../widgets/button";
import { PhosphorIcon } from "../../widgets/icons";
import { Optional } from "../../types";
import { Spacer } from "../../widgets/spacer";
import SolidMarkdown from "solid-markdown";
import { openIntercom } from "~/utils/third_party/intercom";
import { useClassicContext } from "~/data/classic_context";
import rehypeRaw from "rehype-raw";

type FAQ = {
  question: string;
  answer: string;
};

type HelpContent = {
  videos: any[];
  faqs: FAQ[];
};

type CollapsibleContent = {
  header: string;
  body: string;
  isExpanded: boolean;
};

export function HelpAndSupport() {
  const [faqs, setFaqs] = createSignal<FAQ[]>([]);
  const { data } = useClassicContext();

  const supportEnabled = data.isLoggedIn && data.clientConfig.supportEnabled;

  return (
    <ResourceLoader
      fetch={async () => {
        let json_path =
          data.isLoggedIn && data.mode == "sdk" && data.clientConfig.partnerName
            ? `help/help_v4.${data.clientConfig.partnerName}.json`
            : "help/help_v4.json";

        try {
          return await getJson<HelpContent>(json_path);
        } catch (e) {
          // fallback to default help if partner-specific fails
          return await getJson<HelpContent>("help/help_v4.json");
        }
      }}
      onAfterFetch={(helpContent) => {
        setFaqs(helpContent.faqs);
      }}
    >
      {() => {
        return (
          <div class="absolute inset-0 flex flex-col overflow-scroll bg-baseTertiaryLight	p-4 pl-5">
            <HelpAndSupportHeader showContactUs={supportEnabled} />
            <Spacer height={8} />
            <FaqSection faqs={faqs()} />
          </div>
        );
      }}
    </ResourceLoader>
  );
}

type HelpAndSupportHeaderProps = {
  showContactUs: boolean;
};

function HelpAndSupportHeader(props: HelpAndSupportHeaderProps) {
  return (
    <div class="flex flex-row justify-end">
      <BackButton />
      <Show when={props.showContactUs} fallback={<Spacer height={32} />}>
        <input
          type="button"
          class="border-silver flex cursor-pointer items-start gap-2.5 rounded bg-[#f0f0f4] px-2.5 py-1 text-mediumBold text-basePrimaryDark "
          value={"Contact us"}
          onClick={() => {
            openIntercom();
          }}
        />
      </Show>
    </div>
  );
}

type FaqAccordionProps = {
  toggleAccordion: (index: number) => void;
  index: () => number;
  faq: FAQ;
  isAccordionItemActive: (index: number) => boolean;
};

function FaqAccordion<T>(props: FaqAccordionProps) {
  let { toggleAccordion, index, faq, isAccordionItemActive } = props;

  return (
    <div
      class="flex flex-col items-start rounded-lg border border-baseTertiaryMedium bg-baseTertiaryLight px-4 py-3 "
      onClick={() => toggleAccordion(index())}
    >
      <div class="flex w-full flex-row items-center justify-between text-buttonMediumLight text-baseDark">
        <SolidMarkdown class="markdown" rehypePlugins={[rehypeRaw] as any}>
          {faq.question}
        </SolidMarkdown>
        <span
          class={`${isAccordionItemActive(index()) ? "flex -rotate-180 [transition:transform_300ms]" : "flex [transition:transform_300ms]"}`}
        >
          <PhosphorIcon name="caret-down" />
        </span>
      </div>

      {isAccordionItemActive(index()) && (
        <div class="animate-expand text-buttonMediumBase text-baseDark">
          <SolidMarkdown class="markdown" rehypePlugins={[rehypeRaw] as any}>
            {faq.answer}
          </SolidMarkdown>
        </div>
      )}
    </div>
  );
}

function FaqSection(props: { faqs: FAQ[] }) {
  const [activeIndex, setActiveIndex] = createSignal<Optional<number>>(null);

  const toggleAccordion = (index: number) => {
    setActiveIndex(activeIndex() === index ? null : index);
  };

  const isAccordionItemActive = (index: number) => activeIndex() === index;

  return (
    <div>
      <div class="py-1 text-h4 text-basePrimaryDark">Help &amp; Support</div>
      <Spacer height={12} />
      <div class="text-small text-basePrimaryLight">POPULAR QUESTIONS</div>
      <Spacer height={8} />
      <div class="flex w-full flex-col gap-2">
        <For each={props.faqs}>
          {(faq, index) => (
            <FaqAccordion
              toggleAccordion={toggleAccordion}
              index={index}
              faq={faq}
              isAccordionItemActive={isAccordionItemActive}
            />
          )}
        </For>
      </div>
    </div>
  );
}
